var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defaultSocketOptions, } from './framework/CommandSocket';
import { Timeouts } from './framework/Timeouts';
export class PlanarHbSeries {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.isMacAddressSpecified = false;
        this.selectedSource = undefined;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayFreeze = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kz 00 ff`, /kz\s+\d{2}\s+(01|00)/i, tags);
                return match[1] === '01';
            }),
            set: (freeze, tags) => __awaiter(this, void 0, void 0, function* () {
                this.busyTimeout.clear();
                const match = yield this.socket.send(`kz 00 ${freeze ? '01' : '00'}`, /kz\s+\d{2}\s+(01|00)/i, tags);
                return match[1] === '01';
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`ka 00 ff`, /ka\s+\d{2}\s+(00|01)/i, tags);
                return match[1] === '01';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Clear any pending busy timeouts that would set busy back to false. The power set
                // methods will set busy to true and manage the transition back to false.
                this.busyTimeout.clear();
                if (this.isMacAddressSpecified) {
                    if (this.socket.options.allowNoConnection) {
                        return this.setPowerWakeOnLanNetwork(power, tags);
                    }
                }
                return this.setPowerSimple(power, tags);
            }),
            powerOffRestrictions: (config) => {
                return this.socket.state === 'connected' ? [] : ['get'];
            },
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kb 00 ff`, /kb\s+\d{2}\s+([0-9a-f]{2})/i, tags);
                this.selectedSource = match[1];
                return match[1];
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                // Don't switch to the same source on the local device since Planar displays blank the
                // screen temporarily when switching.
                if (this.selectedSource !== code) {
                    const match = yield this.socket.send(`kb 00 ${code}`, /kb\s+\d{2}\s+([0-9a-f]{2})/i, tags);
                    this.selectedSource = code;
                    this.state.busy.update(true);
                    this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                    return match[1];
                }
                return this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kf 00 ff`, /kf\s+\d{2}\s+([0-9a-f]+)/i, tags);
                return parseInt(match[1], 16);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kf 00 ${volume.toString(16).padStart(2, '0')}`, /kf 00 ([0-9a-f]+)/i, tags);
                return parseInt(match[1], 16);
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`ke 00 ff`, /ke\s+\d{2}\s+(00|01)/i, tags);
                return match[1] === '01';
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                this.busyTimeout.clear();
                const match = yield this.socket.send(`ke 00 ${mute ? '01' : '00'}`, /ke\s+\d{2}\s+(00|01)/i, tags);
                return match[1] === '01';
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 2500, controls: ['power', 'videoInput'] },
            {
                intervalMs: 5000,
                pollWhenDisconnected: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.state.power.value &&
                        this.socket.options.allowNoConnection &&
                        this.socket.getState() !== 'connected') {
                        if (!this.framework.isReconnecting)
                            yield this.framework.reconnect();
                    }
                }),
            },
        ];
        if (this.socket.options.allowNoConnection) {
            // If the display is powered off externally (e.g., IR remote or third party controller),
            // then we will lose the socket connection. If this happens, we update the power state
            // to false.
            this.socket.onConnectionLost(() => {
                this.state.power.update(false, true);
            });
        }
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.selectedSource = undefined;
            this.state.busy.update(false);
            this.isMacAddressSpecified = config.macAddress !== undefined;
            if (this.isMacAddressSpecified && config.port === undefined) {
                this.log.error(`mac address configured for wake on lan, but no port specified`);
            }
            // Regardless of connection we can't query the power if the device is powered off. The power
            // is initialized to false, but we need to query it if we can when we first connect.
            if (this.socket.state === 'connected') {
                this.state.power.update(yield this.power.get(['power.get']));
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    // other class methods
    setPowerSimple(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(20000);
            this.socket.removeCommands('poll');
            const match = yield this.socket.send(`ka 00 ${power ? '01' : '00'}`, /ka\s+\d{2}\s+(00|01)/i, tags);
            this.state.busy.update(true);
            const newPower = match[1] === '01';
            this.state.power.update(newPower);
            yield this.timeouts.delay(newPower ? 10000 : 2000);
            this.state.busy.update(false);
            return this.state.power.value;
        });
    }
    setPowerWakeOnLanNetwork(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(power ? 25000 : 5000);
            this.socket.removeCommands('poll');
            if (power) {
                // Send a power command without an expectation of receiving a response.
                if (this.socket.state === 'connected') {
                    yield this.socket.sendAsync(`ka 00 01`, tags);
                }
                // For direct Ethernet connections where the socket connection is lost when the display
                // is powered off, we first uppdate the power state so the UI will reflect that the
                // power is on, then we block while sending a wake on lan request and attempting to
                // reconnect to the display.
                this.state.busy.update(true);
                // Update power with a short delay so that framework.reconnect gets called first. This
                // tells StateBridge we are disconnected so it doesn't change phase back to configuring
                // to query the controls with power off restrictions when it sees that the power is
                // back on.
                this.timeouts.set(() => this.state.power.update(power), 100);
                yield this.framework.reconnect({
                    attemptDelayMs: 2000,
                    initialDelayMs: 0,
                    numAttempts: 8,
                    onBeforeReconnect: () => __awaiter(this, void 0, void 0, function* () {
                        return this.framework.sendWakeOnLan();
                    }),
                });
                // When updating the busy state, we must use notifyIfUnchanged = true. This is because
                // the driver reconnects as part of powering on, and reconnecting resets all the states.
                // Without notifyIfUnchanged = true, the busy = false notification won't get sent.
                this.state.busy.update(false, true);
                return this.socket.state === 'connected' ? this.power.get(tags) : false;
            }
            else {
                const match = yield this.socket.send(`ka 00 ${power ? '01' : '00'}`, /ka\s+\d{2}\s+(00|01)/i, tags);
                this.state.busy.update(true);
                const newPower = match[1].toUpperCase() === '01';
                this.state.power.update(newPower);
                yield this.timeouts.delay(5000);
                this.state.busy.update(false);
                return this.state.power.value;
            }
        });
    }
}
// static properties
PlanarHbSeries.driverType = 'socketDriver';
PlanarHbSeries.key = '-NTEzW5u1WpyO0dyLBa8';
PlanarHbSeries.socketOptions = (config) => {
    const baseSocketOptions = {
        commandExtractor: {
            terminators: ['\r', '\n'],
        },
        sendTerminator: '\r\n',
    };
    const hasMacAddress = config.macAddress !== undefined;
    if (config.port === undefined) {
        return [
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4660, allowNoConnection: hasMacAddress }),
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4999, idleTimeoutMs: hasMacAddress ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: false }),
        ];
    }
    // Assume port 4660 is direct Ethernet to the device and anything else is via an Ethernet
    // to serial converter.
    return [
        Object.assign(Object.assign({}, baseSocketOptions), { port: config.port, idleTimeoutMs: hasMacAddress && config.port !== 4660 ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: hasMacAddress && config.port === 4660 }),
    ];
};
